export type Permission = (typeof Permission)[keyof typeof Permission];

/**
 * Permissions follow a consistent naming pattern of <resource>:<action>, where the resource
 * represents the entity being acted upon, and the action represents the specific operation
 * or capability associated with that resource.
 */
export const Permission = {
  // Account
  ACCOUNT_READ_TENANT_ID: 'account:read_tenant_id',
  ACCOUNT_SWITCH_TENANT: 'account:switch_tenant',

  // Processing
  PROCESSING_START_STATISTICS: 'processing:start_statistics',

  // Session
  SESSION_DELETE: 'session:delete',
  SESSION_EDIT: 'session:edit',
  SESSION_READ_PROCESSINGS: 'session:read_processings',
  SESSION_READ_RESOURCES: 'session:read_resources',

  // Team
  TEAM_EDIT: 'team:edit',

  // Tenant
  TENANT_INVITE: 'tenant:invite',
  TENANT_READ_USERS: 'tenant:read_users',
  TENANT_READ_ALL_USERS: 'tenant:read_all_users',
  TENANT_UPDATE_ATTRIBUTES: 'tenant:update_attributes',
  TENANT_UPDATE_USER: 'tenant:update_user',

  // Video integrations
  INTEGRATION_ACTIVATE: 'integration:activate',
  INTEGRATION_ADD_ONE: 'integration:add_one',
  INTEGRATION_DELETE: 'integration:delete',
  INTEGRATION_EDIT: 'integration:edit',
  INTEGRATION_EDIT_SYNC_CURSOR: 'integration:edit_sync_cursor',
  INTEGRATION_READ: 'integration:read',
  INTEGRATION_READ_CAMERA_IDS: 'integration:read_camera_ids',
  INTEGRATION_READ_ERROR: 'integration:read_error',

  // Labpano integrations
  LABPANO_INTEGRATION_AUTHENTICATE: 'labpano_integration:authenticate',

  // PlaySight integrations
  PLAYSIGHT_INTEGRATION_CAMERA_SELECTION: 'playsight_integration:camera_selection',
  PLAYSIGHT_INTEGRATION_READ_CAMERA_PREVIEW: 'playsight_integration:read_camera_preview',

  // Synergy integrations
  SYNERGY_INTEGRATION_CAMERA_SELECTION: 'synergy_integration:camera_selection',
  SYNERGY_INTEGRATION_UPDATE_TOKEN: 'synergy_integration:update_token',

  // URL upload integrations
  URL_UPLOAD_INTEGRATION_UPLOAD_VIDEO: 'url_upload_integration:upload_video',

  // File upload integrations
  FILE_UPLOAD_INTEGRATION_UPLOAD_VIDEO: 'file_upload_integration:upload_video',

  // Reports
  REPORT_DELETE: 'report:delete',
  REPORT_EXPORT_PDF: 'report:export_pdf',

  // Roster
  ROSTER_CREATE: 'roster:create',
  ROSTER_CREATE_GROUPS: 'roster:create_groups',
  ROSTER_DELETE: 'roster:delete',
  ROSTER_EDIT: 'roster:edit',
} as const;

/**
 * Check if the input string matches any value of the `Permission` object.
 *
 * @param input - The input string to be checked.
 * @returns `true` if the input matches a `Permission` value, `false` otherwise.
 */
export const isPermission = (input: string): input is Permission => {
  return Object.values(Permission).includes(input as Permission);
};
